<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { page } from '$stores/pageStore';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, fetchStudyProperties } from '$stores/configStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '../lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faRotate,
    faSearch,
    faCircleXmark
  } from '@fortawesome/pro-light-svg-icons';
  import { exportComplianceReportToExcel, generateExportFilename } from '$utils/export.js';
  const browser = typeof window !== 'undefined';

  const API_BASE_URL = '/api/proxy';

  let complianceData = [];
  let resultCount = 0;
  let isLoading = true;
  let searchText = '';
  let isExporting = false;
  let hasError = false;
  let errorMessage = '';
  let selectedType = 'ALL';

  let currentFetch = null;
  let isInitialized = false;

  // Subscribe to selectedStudy changes to fetch properties
  $: if ($selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  } else {
    studyProperties.set(null);
  }

  // Add cleanup on component destroy
  onDestroy(() => {
    if (currentFetch) {
      currentFetch.abort();
    }
  });

  // Add a store subscription, but only in the browser
  $: if (browser && isInitialized && $selectedStudy) {
    fetchComplianceData().catch(err => {
      console.error('Error in store subscription:', err);
      hasError = true;
      errorMessage = err.message;
    });
  }

  onMount(async () => {
    if (!browser) return;

    try {
      selectedParticipant.set(null);

      // Get search text from URL parameter if it exists
      const urlSearchText = $page.url.searchParams.get('search');
      if (urlSearchText) {
        searchText = urlSearchText;
      }

      if (!$selectedStudy) {
        goto('/studies');
        return;
      }

      // Mark as initialized after initial setup
      isInitialized = true;
    } catch (err) {
      console.error('Error in onMount:', err);
      hasError = true;
      errorMessage = err.message;
    }
  });

  // Create an abort controller factory
  function createAbortController() {
    if (currentFetch) {
      currentFetch.abort();
    }
    return new AbortController();
  }

  async function fetchComplianceData() {
    const requestId = Date.now();

    const abortController = createAbortController();
    currentFetch = abortController;

    if (!$selectedStudy || !$selectedStudy._id) {
      console.warn(`[Request ${requestId}] No study selected or invalid study data`);
      complianceData = [];
      resultCount = 0;
      return;
    }

    if (!API_BASE_URL) {
      console.error(`[Request ${requestId}] Missing API configuration`);
      return;
    }

    try {
      isLoading = true;
      // Fetch all data at once (no pagination)
      let url = `${API_BASE_URL}/complianceReport/trafficLight?page=1&pageSize=999999&searchText=${searchText}&studyGUID=${$selectedStudy._id}`;

      // Add status filter if not ALL
      if (selectedType !== 'ALL') {
        url += `&subjectStatus=${selectedType}`;
      }

      const res = await fetch(url, {
        signal: abortController.signal
      });

      // Check if this is still the current fetch
      if (currentFetch !== abortController) {
        console.log(`[Request ${requestId}] Fetch was superseded by newer request`);
        return;
      }

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error(`[Request ${requestId}] API Error Response:`, {
          status: res.status,
          statusText: res.statusText,
          errorData
        });
        throw new Error(`Failed to fetch compliance data: ${res.status} ${errorData.message || ''}`);
      }

      const responseData = await res.json();

      // Only update state if this is still the current fetch
      if (currentFetch === abortController) {
        resultCount = responseData.totalCount || 0;
        complianceData = responseData.data || [];
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        console.log(`[Request ${requestId}] Fetch was aborted`);
        return;
      }
      console.error(`[Request ${requestId}] Error fetching compliance data:`, err);
      complianceData = [];
      resultCount = 0;
    } finally {
      if (currentFetch === abortController) {
        isLoading = false;
        currentFetch = null;
      }
    }
  }

  function handleRefresh() {
    fetchComplianceData();
  }

  function handleSearch() {
    const url = new URL(window.location);
    if (searchText) {
      url.searchParams.set('search', searchText);
    } else {
      url.searchParams.delete('search');
    }
    goto(url.toString(), { replaceState: true });
    fetchComplianceData();
  }

  function handleClearSearch() {
    searchText = '';
    handleSearch();
  }

  function handleParticipantClick(item) {
    if (item.siteGUID) {
      const site = {
        _id: item.siteGUID,
        siteId: item.siteId,
        country: item.country
      };
      selectedSite.set(site);
    }
    selectedParticipant.set({ _id: item._id, subjectId: item.subjectId });
    goto(`/person`);
  }

  function getTrafficLightClass(color) {
    switch (color) {
      case 'red':
        return 'bg-red-500';
      case 'yellow':
        return 'bg-yellow-400';
      case 'green':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  }

  function getTrafficLightLabel(color) {
    switch (color) {
      case 'red':
        return 'No compliant days';
      case 'yellow':
        return '1-2 compliant days';
      case 'green':
        return '3+ compliant days';
      default:
        return 'N/A';
    }
  }

  function formatDateRange(startDate, endDate, timezoneCode = 'UTC') {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { month: 'short', day: 'numeric', timeZone: timezoneCode };
    const startStr = start.toLocaleDateString('en-US', options);
    const endStr = end.toLocaleDateString('en-US', options);
    if (startStr === endStr) return 'Invalid Range';
    return `${startStr} - ${endStr}`;
  }

  async function handleExportExcel() {
    try {
      isExporting = true;

      if (!$selectedStudy || !$selectedStudy._id) {
        throw new Error('No study selected');
      }

      // Fetch all data for export
      let url = `${API_BASE_URL}/complianceReport/trafficLight?page=1&pageSize=999999&searchText=${searchText}&studyGUID=${$selectedStudy._id}`;

      if (selectedType !== 'ALL') {
        url += `&subjectStatus=${selectedType}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch compliance data for export');
      }

      const responseData = await res.json();
      const allData = responseData.data || [];

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        reportType: 'ComplianceTrafficLight'
      });

      const formattedData = allData.map((item) => ({
        Protocol: $selectedStudy?.protocolNumber || '',
        'Site ID': item.siteId || '',
        Country: item.country || '',
        Participant: item.subjectId || '',
        Status: item.statusLabel || '',
        'Latest Visit': item.latestVisit || '',
        'Run-in Period': item.runIn?.hasData ? formatDateRange(item.runIn?.startDate, item.runIn?.endDate, item.timezoneCode) : '',
        'Run-in Compliant Days': item.runIn?.hasData ? item.runIn.compliantDays : '',
        'Run-in Traffic Light': item.runIn?.hasData ? (item.runIn.trafficLight || '').toUpperCase() : '',
        'Run-in Status': item.runIn?.hasData ? getTrafficLightLabel(item.runIn.trafficLight) : '',
        'End-point Period': item.endPoint?.hasData ? formatDateRange(item.endPoint?.startDate, item.endPoint?.endDate, item.timezoneCode) : '',
        'End-point Compliant Days': item.endPoint?.hasData ? item.endPoint.compliantDays : '',
        'End-point Traffic Light': item.endPoint?.hasData ? (item.endPoint.trafficLight || '').toUpperCase() : '',
        'End-point Status': item.endPoint?.hasData ? getTrafficLightLabel(item.endPoint.trafficLight) : ''
      }));

      await exportComplianceReportToExcel(
        formattedData, 
        filename, 
        ['Run-in Traffic Light', 'End-point Traffic Light']
      );
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

{#if hasError}
  <div class="p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 rounded-lg mb-4">
    <p>An error occurred: {errorMessage}</p>
    <button
      class="mt-2 px-4 py-2 bg-red-200 dark:bg-red-800 rounded hover:bg-red-300 dark:hover:bg-red-700"
      on:click={() => {
        hasError = false;
        errorMessage = '';
        if ($selectedStudy) {
          fetchComplianceData();
        }
      }}
    >
      Try Again
    </button>
  </div>
{/if}

<StudyHeader />

<FeatureInfo
  title="CGM Compliance Traffic Light Report"
  description="This report shows CGM data compliance using traffic light indicators for two key periods: Run-in (Visit 4-6) and End-point (Visit 28-32). 
  Green indicates 3+ days with 90% data, Yellow indicates 1-2 days, Red indicates no compliant days, and Gray indicates missing enrollment data."
  marginBottom="mb-0"
/>

{#if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<Toolbar title="Period Compliance Report" itemCountTitle="Participants" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="flex items-center gap-2">
      <div class="relative">
        <input
          type="text"
          placeholder="Search"
          bind:value={searchText}
          on:keydown={(e) => e.key === 'Enter' && handleSearch()}
          class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
        />
        {#if searchText}
          <button
            on:click={handleClearSearch}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Clear search"
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        {/if}
      </div>
      <button on:click={handleSearch} title="Search">
        <FontAwesomeIcon icon={faSearch} />
      </button>
    </div>
    <div class="flex items-baseline gap-2">
      Status:
      <select
        bind:value={selectedType}
        on:change={() => fetchComplianceData()}
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL" selected>All</option>
        <option value="ACTIVE">Active</option>
        <option value="COMPD">End of Participation</option>
      </select>
    </div>
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if isLoading}
    <div class="flex justify-center items-center py-8">
      <div class="loader"></div>
    </div>
  {:else if complianceData.length > 0}
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site ID</th>
          <th class="px-4 py-2 text-left bold">Country</th>
          <th class="px-4 py-2 text-left bold">Participant</th>
          <th class="px-4 py-2 text-left bold">Latest Visit</th>
          <th class="px-4 py-2 text-left bold">Run-in Period<br/><span class="text-xs font-normal">(Visit 4-6)</span></th>
          <th class="px-4 py-2 text-left bold">End-point Period<br/><span class="text-xs font-normal">(Visit 28-32)</span></th>
          <th class="px-4 py-2 text-left bold">Status</th>
        </tr>
      </thead>
      <tbody>
        {#each complianceData as item, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{item.siteId || ''}</td>
            <td class="px-4 py-2">{item.country || ''}</td>
            <td class="px-4 py-2 cursor-pointer hover:underline" on:click={() => handleParticipantClick(item)}>
              {item.subjectId}
            </td>
            <td class="px-4 py-2 text-xs">
              {#if item.latestVisit}
                {item.latestVisit}
              {:else}
                —
              {/if}
            </td>
            <td class="px-4 py-2">
              <div class="flex items-center gap-2">
                <div
                  class="w-4 h-4 rounded-full {getTrafficLightClass(item.runIn?.trafficLight)}"
                  title={getTrafficLightLabel(item.runIn?.trafficLight)}
                ></div>
                <span class="text-xs">
                  {#if item.runIn?.hasData}
                    {item.runIn.compliantDays} day{item.runIn.compliantDays !== 1 ? 's' : ''}
                  {:else}
                    N/A
                  {/if}
                </span>
              </div>
            </td>
            <td class="px-4 py-2">
              <div class="flex items-center gap-2">
                <div
                  class="w-4 h-4 rounded-full {getTrafficLightClass(item.endPoint?.trafficLight)}"
                  title={getTrafficLightLabel(item.endPoint?.trafficLight)}
                ></div>
                <span class="text-xs">
                  {#if item.endPoint?.hasData}
                    {item.endPoint.compliantDays} day{item.endPoint.compliantDays !== 1 ? 's' : ''}
                  {:else}
                    N/A
                  {/if}
                </span>
              </div>
            </td>
            <td class="w-px px-4 py-2">
              <span
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                class:text-green-600={item.statusLabel === 'Screened/Enrolled'}
                class:ring-green-600={item.statusLabel === 'Screened/Enrolled'}
                class:text-green-500={item.statusLabel === 'Enrolled'}
                class:ring-green-500={item.statusLabel === 'Enrolled'}
                class:text-gray-600={item.statusLabel === 'Discontinued'}
                class:ring-gray-600={item.statusLabel === 'Discontinued'}
                class:text-gray-500={item.statusLabel === 'Screen Failed'}
                class:ring-gray-500={item.statusLabel === 'Screen Failed'}
                class:text-sky-600={item.statusLabel === 'Completed'}
                class:ring-sky-600={item.statusLabel === 'Completed'}
              >
                {item.statusLabel || ''}
              </span>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      No participants found
    </div>
  {/if}
</div>

<Footer>
  <div></div>
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style>
